import type { DataAttribute } from '../../amplienceTypes/partials/exported/element-attributes-schema';
import type { PropsWithChildren } from 'react';

import { getCookie, setCookie } from 'cookies-next';
import { createContext, memo, use, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

import { HOMEPAGE_PROFILE_TOGGLE, VTA } from '~/utilities/constants/cookies';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';

export type HomepageProfile = 'gender:men' | 'gender:women' | 'default';

type ProfileToggleContextValue = {
  activeProfile: HomepageProfile;
  setProfile: (profile: HomepageProfile) => void;
};

const ProfileToggleContext = createContext<ProfileToggleContextValue | undefined>(undefined);

const profileToggleCookieMaxAge = 60 * 60 * 24 * 365;

const bloomreachProfileValueMap = {
  men: 'male',
  women: 'female',
} as const;

export const normalizeProfile = (value?: string | null): HomepageProfile => {
  switch (value?.toLowerCase()) {
    case 'gender:men':
    case 'men':
      return 'gender:men';
    case 'gender:women':
    case 'women':
      return 'gender:women';
    default:
      return 'default';
  }
};

export const matchesProfileToggle = (
  profileToggleValue: string | undefined,
  activeProfile: HomepageProfile
): boolean => {
  const allowedProfiles = profileToggleValue
    ?.split(',')
    .map(profile => normalizeProfile(profile.trim()))
    .filter(Boolean);

  return !allowedProfiles?.length || allowedProfiles.includes(activeProfile);
};

export const getProfileToggleValue = (
  dataAttributes: DataAttribute[] | undefined
): string | undefined =>
  dataAttributes?.find(dataAttribute => dataAttribute.name === 'data-profile-toggle')?.value;

export const useProfileToggleContext = (): ProfileToggleContextValue => {
  const context = use(ProfileToggleContext);

  if (!context) {
    throw new Error(
      '`useProfileToggleContext()` is used outside of a `<ProfileToggleProvider />`.'
    );
  }

  return context;
};

export const useOptionalProfileToggleContext = (): ProfileToggleContextValue | undefined =>
  use(ProfileToggleContext);

export const useMatchesProfileToggle = (dataAttributes: DataAttribute[] | undefined): boolean => {
  const profileToggleContext = useOptionalProfileToggleContext();
  const activeProfile = profileToggleContext?.activeProfile ?? 'default';
  const profileToggleValue = getProfileToggleValue(dataAttributes);

  return matchesProfileToggle(profileToggleValue, activeProfile);
};

export const ProfileToggleProvider = memo(({ children }: PropsWithChildren) => {
  const [activeProfile, setActiveProfile] = useState<HomepageProfile>('default');
  const { hasAnalyticsConsent } = useConsentContext();
  const { pushToDataLayer } = useDataLayerContext();

  useEffect(() => {
    const homepageProfileCookie = hasAnalyticsConsent
      ? getCookie(HOMEPAGE_PROFILE_TOGGLE)?.toString()
      : undefined;
    const vtaCookie = hasAnalyticsConsent ? getCookie(VTA)?.toString() : undefined;
    const homepageProfile = normalizeProfile(homepageProfileCookie);
    const vtaProfile = normalizeProfile(vtaCookie);
    const nextProfile = homepageProfile === 'default' ? vtaProfile : homepageProfile;

    setActiveProfile(nextProfile);
  }, [hasAnalyticsConsent]);

  const value = useMemo(
    (): ProfileToggleContextValue => ({
      activeProfile,
      setProfile: nextProfile => {
        setActiveProfile(nextProfile);

        if (hasAnalyticsConsent) {
          setCookie(HOMEPAGE_PROFILE_TOGGLE, nextProfile, { maxAge: profileToggleCookieMaxAge });
        }

        if (nextProfile !== 'default') {
          const [profileType, profileValue] = nextProfile.split(':');

          pushToDataLayer({
            event_name: 'profile_toggle',
            namespace: 'exponea',
            event_properties: {
              profile_type: profileType,
              profile_value:
                bloomreachProfileValueMap[profileValue as keyof typeof bloomreachProfileValueMap],
            },
          });
        }
      },
    }),
    [activeProfile, hasAnalyticsConsent, pushToDataLayer]
  );

  return <ProfileToggleContext value={value}>{children}</ProfileToggleContext>;
}, isEqual);

ProfileToggleProvider.displayName = 'ProfileToggleProvider';
